import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { CategoriesService } from '@/lib/categoriesService';
import { NotesService } from '@/lib/notesService';
import { BackupService } from '@/lib/backupService';
import { DecryptedCategory, StorageStats } from '@/lib/types';
import { LogOut, Key, Mail, Shield, Tag, Trash2, BarChart3, ChevronDown, Download, Upload, HardDrive } from 'lucide-react';
import { ProgressBar } from '@/components/ProgressBar';
import './Settings.css';

export default function Settings() {
  const { user, signOut, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [categories, setCategories] = useState<DecryptedCategory[]>([]);
  const [categoryToDelete, setCategoryToDelete] = useState<DecryptedCategory | null>(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(true);
  const [stats, setStats] = useState<StorageStats>({
    totalNotes: 0,
    notesWithPhotos: 0,
    notesWithVideos: 0,
    totalStorageSize: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [backupPassword, setBackupPassword] = useState('');
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadCategories();
    loadStorageStats();
  }, []);

  async function loadCategories() {
    setIsLoadingCategories(true);
    const cats = await CategoriesService.getCategories();
    setCategories(cats);
    setIsLoadingCategories(false);
  }

  async function loadStorageStats() {
    setIsLoadingStats(true);
    try {
      const totalNotes = await NotesService.getNotesCount();
      const notesWithPhotos = await NotesService.getNotesWithPhotosCount();
      const notesWithVideos = await NotesService.getNotesWithVideosCount();
      const totalSize = await NotesService.getTotalStorageSize();

      setStats({
        totalNotes,
        notesWithPhotos,
        notesWithVideos,
        totalStorageSize: totalSize,
      });
    } catch (error) {
    } finally {
      setIsLoadingStats(false);
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  async function handleLogout() {
    await signOut();
    toast.success('Logout realizado com sucesso!');
    navigate('/login');
  }

  async function handleChangePassword() {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.warning('Preencha todos os campos de senha');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsChangingPassword(true);

    try {
      const { error } = await changePassword(currentPassword, newPassword);

      if (error) throw error;

      toast.success('Senha alterada com sucesso! Sua chave mestra foi re-criptografada.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível alterar a senha');
    } finally {
      setIsChangingPassword(false);
    }
  }

  async function handleDeleteCategory() {
    if (!categoryToDelete) return;

    const success = await CategoriesService.deleteCategory(categoryToDelete.id);

    if (success) {
      toast.success('Categoria deletada com sucesso!');
      await loadCategories();
      setCategoryToDelete(null);
    } else {
      toast.error('Não foi possível deletar a categoria');
    }
  }

  async function handleCreateBackup() {
    if (!backupPassword) {
      toast.warning('Digite sua senha para criar o backup');
      return;
    }

    setIsCreatingBackup(true);
    setBackupProgress(0);

    try {
      setBackupProgress(20);

      const backupBlob = await BackupService.createBackup(backupPassword);

      setBackupProgress(60);

      if (!backupBlob) {
        toast.error('Erro ao criar backup. Verifique sua senha.');
        return;
      }

      setBackupProgress(80);

      if (user?.email) {
        BackupService.downloadBackup(backupBlob, user.email);
      }

      setBackupProgress(100);
      toast.success('Backup criado e baixado com sucesso!');
      setBackupPassword('');

      setTimeout(() => {
        setBackupProgress(0);
      }, 2000);
    } catch (error) {
      toast.error('Erro ao criar backup');
    } finally {
      setIsCreatingBackup(false);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Tamanho máximo: 1GB');
        return;
      }

      const validExtensions = ['.ghpbackup', '.json'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!hasValidExtension) {
        toast.warning(`Arquivo com extensão incomum. Esperado: ${validExtensions.join(' ou ')}`);
      }

      if (file.size > 50 * 1024) {
        toast.warning(`Arquivo grande (${formatFileSize(file.size)}). Backups normais têm ~5KB. Isto pode não ser um backup válido.`);
      }

      setRestoreFile(file);
      toast.success(`Arquivo selecionado: ${file.name}`);
    }
  }

  async function handleRestoreBackup() {
    if (!restoreFile || !restorePassword || !user?.email) {
      toast.warning('Selecione um arquivo de backup e digite sua senha');
      return;
    }

    setShowRestoreConfirm(true);
  }

  async function confirmRestoreBackup() {
    if (!restoreFile || !restorePassword || !user?.email) return;

    setIsRestoring(true);
    setRestoreProgress(0);
    setShowRestoreConfirm(false);

    try {
      const validation = await BackupService.restoreBackup(
        restoreFile,
        restorePassword,
        user.email,
        (progress) => {
          setRestoreProgress(Math.round(progress * 0.4));
        }
      );

      if (!validation.isValid) {
        toast.error(`Erro: ${validation.errors.join('; ')}`);
        setRestoreProgress(0);
        return;
      }

      if (validation.warnings.length > 0) {
        validation.warnings.forEach((warning) => toast.warning(warning));
      }

      const result = await BackupService.applyBackup(
        restoreFile,
        restorePassword,
        user.email,
        (progress) => {
          setRestoreProgress(Math.round(40 + (progress * 0.6)));
        }
      );

      if (result.success) {
        setRestoreProgress(100);
        toast.success('Backup restaurado com sucesso! Recarregando aplicação...');

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(result.error || 'Erro ao aplicar backup');
        setRestoreProgress(0);
      }
    } catch (error) {
      toast.error('Erro inesperado ao restaurar backup');
      setRestoreProgress(0);
    } finally {
      if (!document.location.href.includes('reload')) {
        setIsRestoring(false);
        setRestoreFile(null);
        setRestorePassword('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="settings-title">Configurações</h1>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <div className="section-header">
            <Mail size={20} />
            <h2 className="section-title">Conta</h2>
          </div>
          <div className="info-card">
            <p className="info-label">Email</p>
            <p className="info-value">{user?.email}</p>
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <Key size={20} />
            <h2 className="section-title">Alterar Senha</h2>
          </div>
          <div className="info-card">
            <p className="security-text">
              Alterar sua senha re-criptografará sua chave mestra. Seus arquivos e notas
              permanecerão acessíveis.
            </p>
          </div>
          <div className="input-group">
            <input
              type="password"
              placeholder="Senha atual"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="settings-input"
              disabled={isChangingPassword}
            />
          </div>
          <div className="input-group">
            <input
              type="password"
              placeholder="Nova senha"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="settings-input"
              disabled={isChangingPassword}
            />
          </div>
          <div className="input-group">
            <input
              type="password"
              placeholder="Confirmar nova senha"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="settings-input"
              disabled={isChangingPassword}
            />
          </div>
          <button
            onClick={handleChangePassword}
            disabled={isChangingPassword}
            className="change-password-button"
          >
            <Key size={20} />
            <span>{isChangingPassword ? 'Alterando...' : 'Alterar Senha'}</span>
          </button>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <HardDrive size={20} />
            <h2 className="section-title">Backup e Restauração</h2>
          </div>
          <div className="info-card">
            <p className="security-text">
              🔐 Faça backup da sua chave de criptografia para recuperar suas notas
              em caso de reinstalação ou migração de servidor. O backup é protegido
              com sua senha e contém apenas a chave de criptografia, não suas notas.
            </p>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#333' }}>
              Criar Backup
            </h3>
            <div className="input-group">
              <input
                type="password"
                placeholder="Digite sua senha atual"
                value={backupPassword}
                onChange={(e) => setBackupPassword(e.target.value)}
                className="settings-input"
                disabled={isCreatingBackup}
              />
            </div>
            {isCreatingBackup && backupProgress > 0 && (
              <ProgressBar
                progress={backupProgress}
                message="Criando backup..."
                showPercentage={true}
              />
            )}
            <button
              onClick={handleCreateBackup}
              disabled={isCreatingBackup || !backupPassword}
              className="change-password-button"
              style={{ marginTop: '12px' }}
            >
              <Download size={20} />
              <span>{isCreatingBackup ? 'Criando...' : 'Criar e Baixar Backup'}</span>
            </button>
          </div>

          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#333' }}>
              Restaurar Backup
            </h3>
            <div className="info-card" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
              <p className="security-text" style={{ color: '#856404' }}>
                ⚠️ Atenção: Restaurar um backup substituirá sua chave de criptografia atual.
                Certifique-se de ter feito backup das suas notas atuais antes de continuar.
              </p>
            </div>
            <div className="input-group" style={{ marginTop: '16px' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ghpbackup,.json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={isRestoring}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="change-password-button"
                disabled={isRestoring}
                style={{ width: '100%' }}
              >
                <Upload size={20} />
                <span>{restoreFile ? restoreFile.name : 'Selecionar Arquivo de Backup'}</span>
              </button>
            </div>
            {restoreFile && (
              <>
                <div className="input-group">
                  <input
                    type="password"
                    placeholder="Digite sua senha"
                    value={restorePassword}
                    onChange={(e) => setRestorePassword(e.target.value)}
                    className="settings-input"
                    disabled={isRestoring}
                  />
                </div>
                {isRestoring && restoreProgress > 0 && (
                  <ProgressBar
                    progress={restoreProgress}
                    message="Restaurando backup..."
                    showPercentage={true}
                  />
                )}
                <button
                  onClick={handleRestoreBackup}
                  disabled={isRestoring || !restorePassword}
                  className="change-password-button"
                  style={{ marginTop: '12px', background: '#ff6b6b' }}
                >
                  <Upload size={20} />
                  <span>{isRestoring ? 'Restaurando...' : 'Restaurar Backup'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="settings-section">
          <div
            className="section-header accordion-header"
            onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)}
          >
            <div className="section-header-left">
              <Tag size={20} />
              <h2 className="section-title">Categorias</h2>
            </div>
            <ChevronDown
              size={20}
              className={`accordion-icon ${isCategoriesExpanded ? 'expanded' : ''}`}
            />
          </div>
          {isCategoriesExpanded && (
            <>
              {isLoadingCategories ? (
                <div className="info-card">
                  <p className="security-text">Carregando categorias...</p>
                </div>
              ) : categories.length === 0 ? (
                <div className="info-card">
                  <p className="security-text">Nenhuma categoria criada ainda.</p>
                </div>
              ) : (
                <div className="categories-list">
                  {categories.map((category) => (
                    <div key={category.id} className="category-item">
                      <div className="category-info">
                        <div
                          className="category-color"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="category-name">{category.decryptedName}</span>
                      </div>
                      <button
                        onClick={() => setCategoryToDelete(category)}
                        className="delete-category-button"
                        title="Deletar categoria"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="settings-section">
          <div className="section-header">
            <BarChart3 size={20} />
            <h2 className="section-title">Estatísticas de Uso</h2>
          </div>
          {isLoadingStats ? (
            <div className="info-card">
              <p className="security-text">Carregando estatísticas...</p>
            </div>
          ) : (
            <div className="stats-grid">
              <div className="info-card">
                <p className="info-label">Total de Notas</p>
                <p className="info-value">{stats.totalNotes}</p>
              </div>
              <div className="info-card">
                <p className="info-label">Notas com Fotos</p>
                <p className="info-value">{stats.notesWithPhotos}</p>
              </div>
              <div className="info-card">
                <p className="info-label">Notas com Vídeos</p>
                <p className="info-value">{stats.notesWithVideos}</p>
              </div>
              <div className="info-card">
                <p className="info-label">Espaço Utilizado</p>
                <p className="info-value">{formatFileSize(stats.totalStorageSize)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="settings-section">
          <div className="section-header">
            <Shield size={20} />
            <h2 className="section-title">Segurança</h2>
          </div>
          <div className="info-card">
            <p className="security-text">
              🔒 Suas notas são criptografadas usando sua senha. Ninguém além
              de você pode acessá-las, nem mesmo nossos servidores.
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="logout-button"
        >
          <LogOut size={20} />
          <span>Sair da Conta</span>
        </button>
      </div>

      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Confirmar logout</h2>
            <p className="modal-message">
              Deseja realmente sair da sua conta?
            </p>
            <div className="modal-actions">
              <button onClick={() => setShowLogoutConfirm(false)} className="modal-cancel">
                Cancelar
              </button>
              <button onClick={handleLogout} className="modal-confirm-logout">
                Sair
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryToDelete && (
        <div className="modal-overlay" onClick={() => setCategoryToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Deletar Categoria</h2>
            <p className="modal-message">
              Deseja realmente deletar a categoria "{categoryToDelete.decryptedName}"?
              <br />
              <strong>As notas desta categoria não serão deletadas.</strong>
            </p>
            <div className="modal-actions">
              <button onClick={() => setCategoryToDelete(null)} className="modal-cancel">
                Cancelar
              </button>
              <button onClick={handleDeleteCategory} className="modal-confirm">
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}

      {showRestoreConfirm && (
        <div className="modal-overlay" onClick={() => setShowRestoreConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Confirmar Restauração</h2>
            <p className="modal-message">
              ⚠️ Deseja realmente restaurar este backup?
              <br /><br />
              <strong>Esta ação substituirá sua chave de criptografia atual.</strong>
              <br /><br />
              Após a restauração, a aplicação será recarregada automaticamente.
            </p>
            <div className="modal-actions">
              <button onClick={() => setShowRestoreConfirm(false)} className="modal-cancel">
                Cancelar
              </button>
              <button
                onClick={confirmRestoreBackup}
                className="modal-confirm"
                style={{ background: '#ff6b6b' }}
              >
                Restaurar Backup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
